import DigitalCreditCardFlyoutSvg from 'ext-ui/svg/illustration/DigitalCreditCardFlyoutSvg'
import moment from 'moment-timezone'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { UpdateVirtualCardScreen } from 'src/app/UpdateVirtualCardForm/UpdateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import TestId from 'src/components/TestId/TestId'
import { UserOption } from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { VirtualCardTypes } from 'src/enums'
import Trns, { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { UserListModel } from 'src/models/user.models'
import {
  ApprovalStatus,
  VirtualCardSecureModel,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import { virtualCardsAllCardsUrl } from 'src/navigation/urls'
import {
  selectNullSafeCurrencyFromState,
  selectUserIdFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectSelectedVirtualCardIdsFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PutVirtualCardRequest } from 'src/requests/putVirtualCard.request'
import { toCents, toDollars } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { css } from 'styled-components'

import styles from './UpdateVirtualCardIssuerCycleContent.style'

interface UpdateVirtualCardIssuerCycleContentProps {
  cardApproverId: string | undefined
  currencyError?: boolean
  issuerCycleOptions?: CreditCardListModel[]
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions: VirtualCardPermissionsModel
  recipient: UserListModel
  cardholder: UserListModel
  userOptions: UserOption[]
  isFormDisabled: boolean
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  puttingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  putVirtualCardAndOpenToastAndCloseFlyout: (
    id: string,
    body: Partial<PutVirtualCardRequest>,
    openToast?: boolean,
  ) => void
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  setScreen: (val: UpdateVirtualCardScreen) => void
  negativeButton?: {
    loading: boolean
    text: React.ReactElement | string
    onClick: () => void
  }
  creditCardInfiniteScrollProps?: CreditCardDropdownInfiniteScrollProps
  showSoftAlert?: boolean
}

const UpdateVirtualCardIssuerCycleContent: React.FC<
  UpdateVirtualCardIssuerCycleContentProps
> = (props) => {
  const defaultCurrency = useSelector(selectNullSafeCurrencyFromState)
  const selectedVirtualCardIdsFromRedux = useSelector(
    selectSelectedVirtualCardIdsFromState,
  )
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const { analytics } = useAnalyticsContext()
  const userId = useSelector(selectUserIdFromState)

  // Track the form's selected credit card — needed for pending VCNs where
  // virtualCard.creditCardId is undefined (requester didn't specify funding source).
  // Note: useState initializer only — this component remounts when a different
  // virtual card is opened (keyed by virtualCard.id in parent), so stale state
  // is not a concern. Updated via onCreditCardChange callback from the form.
  const [selectedCreditCardId, setSelectedCreditCardId] = useState<
    string | undefined
  >(props.virtualCard.creditCardId)

  const isCurrentUserCardApprover = userId === props.cardApproverId
  const isCurrentUserCardRequester = userId === props.virtualCard.recipientId
  const isFinalCardApprovalOrRejection =
    isCurrentUserCardApprover &&
    props.virtualCard.awaitingApprovalStatus ===
      ApprovalStatus.AwaitingApproverRuleApproval

  // Pre-populate with pending limit if available, otherwise current credit limit.
  // For issuer cycle cards, the form field is "Monthly Credit Limit" — use limitCents,
  // not balanceCents (which is the remaining balance after spending).
  const initialBalanceDollars = toDollars(
    props.virtualCard.pending?.limitCents ?? props.virtualCard.limitCents,
  )

  const baseFlatFields = useMemo(
    (): VirtualCardFlatFormValues => ({
      displayName: props.virtualCard.displayName,
      userEmails: props.virtualCardPermissions.canUpdateVirtualCard
        ? [props.recipient.email]
        : props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
          ? [props.cardholder.email, props.recipient.email]
          : [props.cardholder.email],
      creditCardId: props.virtualCard.creditCardId,
      balanceDollars: props.virtualCard.limit,
      notes: props.virtualCard.notes,
      validToMoment: props.virtualCard.validToTimezonedMoment,
      currency: props.virtualCard.currency,
    }),
    [props.virtualCard, props.recipient, props.virtualCardPermissions],
  )

  const initialFlatFields = useMemo(
    (): VirtualCardFlatFormValues => ({
      displayName: props.virtualCard.displayName,
      userEmails: props.virtualCardPermissions.canUpdateVirtualCard
        ? [props.recipient.email]
        : props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
          ? [props.cardholder.email, props.recipient.email]
          : [props.cardholder.email],
      creditCardId: props.currencyError
        ? undefined
        : props.virtualCard.creditCardId,
      balanceDollars: props.currencyError ? 0 : initialBalanceDollars,
      notes: props.virtualCard.notes,
      validToMoment:
        props.virtualCard.pending?.validToTimezonedMoment(
          props.virtualCard.timezone,
        ) || props.virtualCard.validToTimezonedMoment,
      currency: props.currencyError
        ? defaultCurrency
        : props.virtualCard.currency,
      receiptRulesExempt: props.virtualCard.receiptRulesExempt,
    }),
    [
      props.virtualCard,
      props.recipient,
      props.virtualCardPermissions,
      initialBalanceDollars,
      defaultCurrency,
      props.currencyError,
    ],
  )

  const buttonText = props.virtualCard.updateFormButtonText(
    props.virtualCardPermissions,
    isCurrentUserCardApprover,
  )

  const buttonId = props.virtualCardPermissions.canUpdateVirtualCard
    ? 'UpdateCard'
    : 'RequestCardUpdate'

  const translate = useTranslate()

  const selectedCcId = selectedCreditCardId ?? props.virtualCard.creditCardId
  const selectedCreditCardFromRedux =
    selectedCcId != null ? creditCards[selectedCcId] : undefined

  const resetsOnMoment = useMemo(() => {
    // Priority: virtual card's own config first (the card's actual cycle date),
    // then fall back to the selected account's config.
    const fromReactQuery = props.issuerCycleOptions?.find(
      (cc) => cc.id === selectedCcId,
    )
    const nextAt =
      props.virtualCard.issuerCycleConfig?.nextAt ??
      fromReactQuery?.issuerCycleConfig?.nextAt ??
      selectedCreditCardFromRedux?.issuerCycleConfig?.nextAt
    if (nextAt == null) return undefined
    return moment.tz(nextAt, props.virtualCard.timezone)
  }, [
    selectedCcId,
    props.issuerCycleOptions,
    props.virtualCard.issuerCycleConfig?.nextAt,
    props.virtualCard.timezone,
    selectedCreditCardFromRedux,
  ])

  const resetsOnDayOrdinal: string = useMemo(() => {
    if (resetsOnMoment == null) return '--'
    return resetsOnMoment.format('Do')
  }, [resetsOnMoment])

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
  ): void => {
    if (isFinalCardApprovalOrRejection && !isCurrentUserCardRequester) {
      props.putVirtualCardAndOpenToastAndCloseFlyout(props.virtualCard.id, {
        pendingRevisionId:
          props.virtualCard?.pending?.id !== undefined
            ? props.virtualCard.pending.id
            : undefined,
      })
    } else {
      // Integer-only validation (EX-38563): ensure no fractional cents
      if (
        flatFields.balanceDollars !== undefined &&
        Math.floor(flatFields.balanceDollars) !== flatFields.balanceDollars
      ) {
        return
      }

      props.putVirtualCardAndOpenToastAndCloseFlyout(props.virtualCard.id, {
        creditCardId: flatFields.creditCardId,
        displayName: flatFields.displayName,
        expenseDetails: Object.entries(expenseDetails)
          .filter((keyVal) => !!keyVal[1])
          .map(([key, val]) => ({ categoryId: key, labelId: val })),
        notes: flatFields.notes,
        balanceCents:
          flatFields.balanceDollars !== undefined
            ? toCents(flatFields.balanceDollars)
            : undefined,
        recurs: false,
        receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
        validTo:
          flatFields.validToMoment !== undefined
            ? flatFields.validToMoment.format('YYYY-MM-DD')
            : undefined,
        currency: flatFields.currency,
        pendingRevisionId: props.virtualCard.pending?.id,
        receiptRulesExempt: flatFields.receiptRulesExempt,
      })
    }

    const eventName =
      props.virtualCardPermissions.canUpdateVirtualCard ||
      isCurrentUserCardApprover
        ? props.virtualCard.isPending
          ? 'manage vcn request'
          : !props.virtualCard.pending && !props.virtualCard.isPending
            ? 'update vcn'
            : undefined
        : props.virtualCard.isPending
          ? 'update vcn request'
          : props.virtualCard.isActive
            ? 'request vcn update'
            : undefined

    const properties = {
      type_of_card: props.virtualCard.cardTypeLabel.props.source,
      card_name: flatFields.displayName,
      credit_limit: '$' + flatFields.balanceDollars,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      add_attachments:
        props.receiptAttachments !== undefined &&
        props.receiptAttachments.length > 0,
      add_notes: flatFields.notes !== undefined,
      ...(eventName === 'manage vcn request'
        ? {
            card_Created: true,
            request_status: 'Approve',
          }
        : {}),
      ...(eventName === 'update vcn'
        ? {
            is_card_creator: props.virtualCard.cardholderId === userId,
          }
        : {}),
    }
    if (eventName !== undefined) {
      analytics?.track(eventName, properties)
    }
  }

  return (
    <div css={styles} className="UpdateVirtualCardIssuerCycleContent">
      <ActionRightFlyoutContent
        title={props.virtualCard.updateFlyoutTitle(
          props.virtualCardPermissions,
        )}
        specificElements={{
          subtitle: translate('Recurring Card'),
          imageComponent: <DigitalCreditCardFlyoutSvg />,
        }}
        cardChangeNotAllowed={
          !props.virtualCard.isPending &&
          props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
        }
        permissions={{
          canUpdateVirtualCard:
            props.virtualCardPermissions.canUpdateVirtualCard &&
            !props.virtualCard.isConsumed,
          isCardPending: props.virtualCard.isPending,
        }}
        screen={UpdateVirtualCardScreen.Pick}
      >
        <TestId testId={'updateVirtualCardIssuerCycleForm'}>
          <VirtualCardForm
            topText={
              <>
                {matchPath(
                  calculatedBackgroundLocation.pathname,
                  virtualCardsAllCardsUrl(),
                ) &&
                  selectedVirtualCardIdsFromRedux !== undefined &&
                  selectedVirtualCardIdsFromRedux?.length > 1 && (
                    <SoftAlertBox
                      css={css`
                        padding: 16px 20px;
                        margin-bottom: 20px;
                      `}
                    >
                      <Trns
                        source={
                          'You can only edit virtual cards you have created or have access to as a budget user'
                        }
                      />
                    </SoftAlertBox>
                  )}
                {!isCurrentUserCardApprover
                  ? props.virtualCard.updateFormTopText(
                      props.virtualCardPermissions,
                    )
                  : undefined}
              </>
            }
            virtualCard={props.virtualCard}
            initialFlatFields={initialFlatFields}
            customAddressEditable
            physicalCardEditable
            updateProps={{
              baseFlatFields: baseFlatFields,
              timezone: props.virtualCard.timezone,
            }}
            buttonText={buttonText}
            id={buttonId}
            onSuccessfulSubmit={onSuccessfulSubmit}
            onCreditCardChange={(creditCardId) => {
              setSelectedCreditCardId(creditCardId)
            }}
            userOptions={props.userOptions}
            userFieldLabel={
              props.virtualCardPermissions.canUpdateVirtualCard ? (
                <Trns source={'Recipient'} />
              ) : (
                <Trns source={'Request From'} />
              )
            }
            loading={props.puttingVirtualCard}
            recipientNamesEditable={false}
            recurrenceEditable={false}
            notesEditable
            amountProps={{
              amountEditable: true,
              amountPlaceholder: translate('Monthly Credit Limit'),
            }}
            // Account is read-only once the card has been created (issued against
            // a specific credit card). For pending requests not yet created,
            // the funding source remains editable.
            creditCardEditable={props.virtualCard.isPending}
            creditCardProps={
              props.virtualCardPermissions.canUpdateVirtualCard
                ? {
                    isFormDisabled: props.isFormDisabled,
                    creditCardOptions: props.creditCardOptions,
                    labelCreditCardsAsAccounts:
                      props.labelCreditCardsAsAccounts,
                  }
                : undefined
            }
            attachmentsProps={{
              receiptAttachments: props.receiptAttachments,
              uploadingAttachmentThumbnails:
                props.uploadingAttachmentThumbnails,
              uploadAttachments: props.uploadAttachments,
              deleteAttachment: props.deleteAttachment,
            }}
            negativeButton={props.negativeButton}
            topLevelError={props.topLevelError}
            errorDetailMap={props.errorDetailMap}
            currencyError={props.currencyError}
            creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
            canUpdateVirtualCard={
              props.virtualCardPermissions.canUpdateVirtualCard
            }
            typeofCard={VirtualCardTypes.issuerCycle}
            showCardApprovalSoftAlert={props.showSoftAlert}
            awaitingFinalApproval={
              props.virtualCard.awaitingApprovalStatus ===
              ApprovalStatus.AwaitingApproverRuleApproval
            }
            awaitingFundingSourceOwnerApproval={
              props.virtualCard.awaitingApprovalStatus ===
              ApprovalStatus.AwaitingCreditCardUserApproval
            }
            cardApproverId={props.cardApproverId}
            isRequest={props.virtualCard.isPending}
            resetsOnHelperText={
              <span>
                {translate(
                  'This will apply to your credit limit instantly. Your credit limit will also reset to this amount on the ${date}.',
                  { date: resetsOnDayOrdinal },
                )}
              </span>
            }
            resetsOnMomentProps={{
              value: resetsOnMoment,
              onChange: () => {},
              placeHolder: '--',
            }}
          />
        </TestId>
      </ActionRightFlyoutContent>
    </div>
  )
}

export default UpdateVirtualCardIssuerCycleContent
